/**
 * PathwayCanvasEdge (Sprint 4.3.5).
 *
 * Custom React Flow edge that renders the pathway transitions with the
 * Awell-Studio-style appearance:
 *   - dashed light-gray stroke (token: surface-border) with arrowhead
 *   - branch labels (IF / ELSE / "yes" / "no" ...) rendered as small
 *     rounded pill chips floating on the edge midpoint via
 *     <EdgeLabelRenderer>
 *
 * The label content is whatever React Flow passes through `label` (the
 * pathway-store edge `label` field). We do NOT special-case "IF" vs
 * "ELSE" beyond styling; both render the same chip so any branch label
 * stays visually consistent. HC6 a11y: the chip carries a stable
 * `data-testid` and `role="status"` so tests can assert on the label
 * text without relying on visual position.
 *
 * HC2 token-only. HC5 zero em / en dashes. Brand-Patch v2: zero TLP1-4.
 */
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  type EdgeProps,
} from 'reactflow';

export function PathwayCanvasEdge(props: EdgeProps) {
  const {
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    label,
    markerEnd,
    selected,
  } = props;

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          stroke: 'var(--cps-edge-stroke, #cbd5e1)',
          strokeWidth: selected ? 2 : 1.5,
          strokeDasharray: '4 4',
        }}
      />
      {label !== undefined && label !== null && label !== '' ? (
        <EdgeLabelRenderer>
          <div
            data-testid={`pathway-edge-label-${id}`}
            data-edge-label={String(label)}
            role="status"
            aria-label={`Branch: ${String(label)}`}
            className="inline-flex items-center rounded-signal-pill bg-surface-base px-gutter-sm py-0.5 text-signal-xs font-semibold uppercase tracking-wider text-text-secondary ring-1 ring-inset ring-surface-border shadow-signal-card"
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: 'all',
            }}
          >
            {String(label)}
          </div>
        </EdgeLabelRenderer>
      ) : null}
    </>
  );
}

/**
 * React Flow `edgeTypes` map. Use `default` so legacy edges (no explicit
 * `type` field) automatically pick up the styled renderer.
 */
export const PATHWAY_EDGE_TYPES = {
  default: PathwayCanvasEdge,
} as const;
